import { useState, useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { RunbookTable } from '@/features/objects/components/RunbookTable'
import { calculateRunbookTimes } from '@/features/objects/utils/calculateRunbookTimes'
import { type MasterRunbookFormData } from './schema'
import type { RunbookTask } from '@/types/entities'

interface MasterRunbookFormProps {
    form: UseFormReturn<MasterRunbookFormData>
}

export function MasterRunbookForm({ form }: MasterRunbookFormProps) {
    const { t } = useTranslation()
    const tasks = form.watch('tasks') || []
    const [localTasks, setLocalTasks] = useState<RunbookTask[]>(tasks)

    // Sync localTasks with form state when tasks change
    // This ensures tasks are loaded when editing (when form.reset is called)
    useEffect(() => {
        const formTasks = form.getValues('tasks') || []
        // Update localTasks when form tasks change
        setLocalTasks(formTasks)
    }, [tasks, form]) // Sync when tasks from form.watch change

    return (
        <div className="space-y-6">
            <div className="max-w-2xl">
                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t('masterRunbooks.name', 'Name')}</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder={t('masterRunbooks.namePlaceholder', 'Enter master runbook name')}
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <div className="max-w-2xl">
                <FormField
                    control={form.control}
                    name="information"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t('masterRunbooks.information')}</FormLabel>
                            <FormControl>
                                <RichTextEditor
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    placeholder={t('masterRunbooks.informationPlaceholder')}
                                    className="[&_.ProseMirror]:min-h-[120px]"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <div className="space-y-2">
                <FormLabel>{t('objects.runbook.tasks')}</FormLabel>
                <RunbookTable
                    tasks={localTasks}
                    onSave={(tasks) => {
                        // Update form state with new tasks (no separate save needed)
                        const tasksWithOffsets = calculateRunbookTimes(tasks)
                        form.setValue('tasks', tasksWithOffsets, { shouldValidate: false, shouldDirty: true })
                        setLocalTasks(tasksWithOffsets)
                    }}
                    autoUpdate={true} // Always auto-update form state
                    onAutoUpdateChange={() => {}} // No-op, always auto-update
                    isSaving={false}
                    hideToolbar={true} // Hide save/auto-update controls in form context
                />
            </div>
        </div>
    )
}
